import { useMutation, useQuery } from "@tanstack/react-query";
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import { ERROR_MESSAGES } from "app/data/common/errorMessages";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import {
  AddDispatchPayload,
  AddDispatchResponse,
  ReviewDispatchResponse,
  StaticLoadResponse,
} from "app/data/static-load/model";
import { toast } from "react-toastify";

export const useReview = (
  division: string,
  weekStartDate: string,
  enabled = true
) => {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [QUERY_KEYS.GET_REVIEW_DISPATCH, division, weekStartDate],
    queryFn: async () => {
      const response = await apiQueryAdapter.query<ReviewDispatchResponse>(
        "/manifest-management/review",
        "GET",
        { params: { division, weekStartDate } }
      );

      return response.data;
    },
    enabled: enabled && !!division && !!weekStartDate,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return { data, isLoading, isFetching, refetch };
};

export const  useReviewDispatch = (onSuccess?: () => void) => {
  return useMutation<
    StaticLoadResponse, // response type
    Error, // error type
    { division: string, weekStartDate: string }  // variables type
  >({
    mutationFn: async ({ division, weekStartDate }) => {
      const response = await apiQueryAdapter.query<StaticLoadResponse>(
        `/manifest-management/dispatch?division=${division}&weekStartDate=${weekStartDate}`,
        "POST",
      );
      return response.data;
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      }
      else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      }
      else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });
};

// Ad-hoc dispatch 
export const useAddDispatch = (onSuccess?: () => void) => {
  return useMutation<AddDispatchResponse, Error, AddDispatchPayload>({
    mutationFn: async (payload) => {
      const response = await apiQueryAdapter.query<AddDispatchResponse>(
        "/manifest-management/add-dispatch",
        "POST",
        { body: payload }
      );

      return response.data;
    },
    onSuccess: () => {
      toast.info("Dispatch added successfully");
      onSuccess?.();
    },
    onError: (e: any) => {
      if (e instanceof Error) {
        toast.error(e.message);
      } else if (e?.response?.data?.message) {
        toast.error(e.response.data.message);
      } else {
        toast.error(ERROR_MESSAGES.COMMON);
      }
    },
  });
};
